/**
 * Efficient Color Swatches Loader
 * Uses dedicated color-swatches API endpoint instead of full product pages
 */

class ColorSwatchesLoader {
  constructor() {
    this.loadedSwatches = new Set();
    this.init();
  }

  init() {
    // Load swatches on page load
    this.loadAllSwatches();
    
    // Load swatches for dynamically added content
    document.addEventListener('recentlyViewed:loaded', (event) => {
      this.loadSwatchesInContainer(event.detail.container);
    });
    
    // Load swatches for recently viewed products
    document.addEventListener('recently-viewed-products:loaded', (event) => {
      this.loadSwatchesInContainer(event.detail.container);
    });
    
    // Also listen for when recently-viewed-products element loads
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if it's a recently-viewed-products element or contains product cards
              if (node.tagName === 'RECENTLY-VIEWED-PRODUCTS' || 
                  node.querySelector('recently-viewed-products') ||
                  node.querySelector('product-card')) {
                this.loadSwatchesInContainer(node);
              }
            }
          });
        }
      });
    });
    
    // Start observing the document body for recently viewed products
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  loadAllSwatches() {
    const swatchElements = document.querySelectorAll('[data-load-color-products]');
    swatchElements.forEach(element => this.loadSwatches(element));
  }

  loadSwatchesInContainer(container) {
    const swatchElements = container.querySelectorAll('[data-load-color-products]');
    swatchElements.forEach(element => this.loadSwatches(element));
  }

  async loadSwatches(element) {
    const url = element.dataset.loadColorProducts;
    
    // Skip if already loaded
    if (this.loadedSwatches.has(url)) return;
    
    try {
      // Convert product URL to color-swatches API endpoint
      const apiUrl = url.includes('?') 
        ? `${url}&view=color-swatches`
        : `${url}?view=color-swatches`;
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const html = await response.text();
      
      // Process and insert swatches
      this.processSwatches(element, html);
      
      // Mark as loaded
      this.loadedSwatches.add(url);
      
    } catch (error) {
      console.warn('Failed to load color swatches:', error);
    }
  }

  processSwatches(element, html) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    const swatchesList = tempDiv.querySelector('[data-color-swatches-from-product-item]');
    
    if (!swatchesList) return;
    
    // Make IDs unique by adding timestamp and random suffix
    const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const sectionId = element.closest('.shopify-section')?.id || 'global';
    
    // Update IDs and references to make them unique
    swatchesList.querySelectorAll('.ProductItem__ColorSwatchItem').forEach((item, index) => {
      const input = item.querySelector('input');
      const label = item.querySelector('label');
      
      if (input && label) {
        const baseId = `color-swatch-${sectionId}-${uniqueSuffix}-${index}`;
        const baseName = `color-swatch-${sectionId}-${uniqueSuffix}`;
        
        input.id = baseId;
        input.name = baseName;
        label.setAttribute('for', baseId);
      }
    });
    
    // Replace content with animation
    element.style.opacity = '0';
    
    setTimeout(() => {
      element.innerHTML = swatchesList.outerHTML;
      element.classList.add('fadeIn');
      element.style.opacity = '';
      
      // Trigger carousel resize if needed
      this.triggerResize(element);
      
    }, 100);
  }

  triggerResize(element) {
    const section = element.closest('[data-section-type]');
    if (!section) return;
    
    const sectionType = section.dataset.sectionType;
    
    // Handle different section types that might need resizing
    if (['product-recommendations', 'featured-collections'].includes(sectionType)) {
      const carousel = section.querySelector('.Carousel');
      if (carousel && window.Flickity) {
        const flickityInstance = window.Flickity.data(carousel);
        if (flickityInstance) {
          setTimeout(() => flickityInstance.resize(), 50);
        }
      }
    }
    
    // Handle any other carousels in the container
    const parentContainer = element.closest('.ProductItemColorSwatch, .Collection, .ProductRecommendations');
    if (parentContainer && parentContainer.carousel?.flickityInstance?.isActive) {
      setTimeout(() => parentContainer.carousel.resize(), 50);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.colorSwatchesLoaderInstance = new ColorSwatchesLoader();
  });
} else {
  window.colorSwatchesLoaderInstance = new ColorSwatchesLoader();
} 